import { useEffect, useState, useCallback } from "react";
import { NavigateFunction, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@/common/styles/style";
import Graph from "@/atoms/graph";
import DataInput from "@/atoms/dataInput";
import { inputDataList } from "./data";
import { styles } from "./style";
import dayjs from "dayjs";
import { useLoading } from "@/hooks/useLoading";
import CircularProgress from "@/molecules/circularProgress";
import BackButton from "@/atoms/backButton";
import { TrendViewType } from "../dashboard/type";
import { formatToApiDateTime, lastWeek, today } from "@/helpers/getUtcOffset";
import { Routes } from "@/router/routes";
import ErrorMessage from "@/atoms/errorMessage";

const checkServerConnection = async (
  trendWidget: TrendViewType,
  startDate: string,
  endDate: string,
  selectedDataKeys: string[],
  navigate: NavigateFunction,
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  setFilteredDataForGraph: React.Dispatch<React.SetStateAction<TrendViewType>>,
  startLoading: () => void,
  stopLoading: () => void
) => {
  startLoading();
  try {
    let queryNames: string = "";

    if (selectedDataKeys.length > 0) {
      selectedDataKeys.forEach((key) => {
        if (!queryNames.includes(key)) {
          queryNames += `&dataNames=${key}`;
        }
      });
    }

    const queryParams = `machineId=${trendWidget.machineId}${queryNames}`;
    const date = `startAt=${startDate}&endAt=${endDate}`;
    const response = await fetch(`/api/dashboard/graph/trend/detail?${queryParams}&${date}`, {
        method: "GET",
        credentials: "include",
      });

    const data = await response.json();
    if (response.ok) {
      console.log(data.message);

      const updatedFilteredData: TrendViewType = {
        machineId: trendWidget.machineId,
        widgetId: trendWidget.widgetId,
        position: trendWidget.position,
        sortId: trendWidget.sortId,
        data: [...data.result],
      };

      setFilteredDataForGraph(updatedFilteredData);
    } else if (response.status === 404) {
      alert(data.message);
      navigate(Routes.ERROR);
    } else if (response.status === 401 || response.status === 403) {
      alert(data.message);
      navigate(Routes.LOGIN);
    } else {
      setError(data.message);
    }
  } catch (error) {
    console.error("サーバ接続エラー:", error);
  } finally {
    stopLoading();
  }
};

export default function TrendExpand() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    trendWidget,
  }: {
    trendWidget: TrendViewType;
  } = location.state || {};

  const [selectedDataKeys, setSelectedDataKeys] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<string>(
    formatToApiDateTime(lastWeek)
  );
  const [endDate, setEndDate] = useState<string>(formatToApiDateTime(today));
  const [filteredDataForGraph, setFilteredDataForGraph] =
    useState<TrendViewType>({});
  const [dataNames, setDataNames] = useState<string[]>([]);
  const { loading, startLoading, stopLoading } = useLoading();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const updatedDataNames: string[] = [];
    if (trendWidget.data) {
      trendWidget.data.forEach((trendItem) => {
        Object.keys(trendItem).forEach((key) => {
          if (!updatedDataNames.includes(key)) {
            updatedDataNames.push(key);
          }
        });
      });
    }
    setDataNames(updatedDataNames);
    setSelectedDataKeys(updatedDataNames);
    checkServerConnection(
      trendWidget,
      startDate,
      endDate,
      updatedDataNames,
      navigate,
      setError,
      setFilteredDataForGraph,
      startLoading,
      stopLoading
    );

    const selecteData = async () => {
      try {
        const response = await fetch(`/api/dashboard/widget/item/${trendWidget.machineId}`, {
            method: "GET",
            credentials: "include",
          }
        );

        const data = await response.json();
        if (response.ok) {
          console.log(data.message);
          const names = data.result.map(
            (item: { dataName: string }) => item.dataName
          );
          setDataNames(names);
        } else {
          console.error("サーバ接続エラー: ", response.statusText);
        }
      } catch (error) {
        console.error("サーバ接続エラー:", error);
      }
    };

    selecteData();
  }, []);

  const handleDateChange = useCallback(
    (type: string) => (value: string) => {
      const dateValue = new Date(value);
      if (type === "start") {
        setStartDate(formatToApiDateTime(dateValue));
      } else if (type === "end") {
        setEndDate(formatToApiDateTime(dateValue));
      }
    },
    []
  );

  const handleSelectDataChange = (index: number, value: string) => {
    setSelectedDataKeys((prevKeys) => {
      const updatedKeys = [...prevKeys];
      updatedKeys[index] = value;
      return updatedKeys;
    });
  };

  const handleButtonClick = () => {
    checkServerConnection(
      trendWidget,
      startDate,
      endDate,
      selectedDataKeys,
      navigate,
      setError,
      setFilteredDataForGraph,
      startLoading,
      stopLoading
    );
  };

  return (
    <Box style={styles.container}>
      <Stack direction="row" sx={styles.titleArea}>
        <BackButton />
        <Typography variant="h2" sx={styles.title}>
          トレンド
        </Typography>
      </Stack>
      <Grid container spacing={2} sx={styles.rowContainer}>
        {Array.from({ length: 5 }, (_, index) => (
          <Grid item xs={2.3} key={index}>
            <Typography>{`データ表示 ${index + 1}`}</Typography>
            <Select
              value={selectedDataKeys[index] || ""}
              onChange={(event) =>
                handleSelectDataChange(index, event.target.value)
              }
              size="small"
              fullWidth
              displayEmpty
            >
              <MenuItem key="None" value={""}>
                None
              </MenuItem>
              {dataNames?.map((name, index) => (
                <MenuItem key={index} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        ))}
        {inputDataList(startDate, endDate).map((data, index) => (
          <Grid item xs={2.3} key={index}>
            <DataInput
              datatype="TextField"
              label={data.label}
              textProps={{
                type: "datetime-local",
                value: dayjs(data.value).format("YYYY-MM-DD HH:mm"),
                sx: styles.dateInput,
              }}
              onChange={handleDateChange(data.onChangeValue)}
            />
          </Grid>
        ))}
        <Grid item xs={6.9}>
          <Button
            variant="contained"
            size="small"
            sx={styles.button}
            onClick={handleButtonClick}
          >
            <Typography variant="button">実行</Typography>
          </Button>
        </Grid>
      </Grid>
      <ErrorMessage error={error} />
      {loading ? (
        <CircularProgress />
      ) : (
        <Box style={styles.graphBox}>
          <Graph
            saved={filteredDataForGraph.data || []}
            startDate={startDate}
            endDate={endDate}
          />
        </Box>
      )}
    </Box>
  );
}
